import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, ChevronsUpDown, DollarSign, Copy, Send, CreditCard, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase, callSupabaseFunction } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { parseCurrencyToCents } from '@/lib/format';
import { InvoiceService } from '@/services/invoiceService';

interface Family {
  id: string;
  name: string;
  primary_contact_email: string;
  billing_preference: 'auto' | 'manual';
}

interface QuickChargeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const QuickChargeModal: React.FC<QuickChargeModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { organizationId } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'manual'>('manual');
  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [organizationHasStripe, setOrganizationHasStripe] = useState(false);

  // Format currency input as user types
  const formatCurrencyInput = (value: string): string => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Handle multiple decimal points - keep only the first one
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    return numericValue;
  };

  // Handle amount input change with formatting
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatCurrencyInput(rawValue);
    setAmount(formattedValue);
  };

  // Load families and check Stripe availability when modal opens
  useEffect(() => {
    if (open && organizationId) {
      loadFamilies();
      checkStripeAvailability();
    }
  }, [open, organizationId]);

  const checkStripeAvailability = async () => {
    if (!organizationId) return;

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('billing_settings->stripe_account_id')
        .eq('id', organizationId)
        .single();

      if (error) throw error;

      console.log('Edge function style query result:', data);
      console.log('Stripe account ID value:', data?.billing_settings?.stripe_account_id);
      console.log('Stripe account ID type:', typeof data?.billing_settings?.stripe_account_id);
      
      const hasStripeAccount = !!(data?.billing_settings?.stripe_account_id);
      console.log('Organization has Stripe Connect:', hasStripeAccount);
      
      setOrganizationHasStripe(hasStripeAccount);
      
      // Initial availability - will be updated when family is selected
      setStripeAvailable(false);
      setPaymentMethod('manual');
    } catch (error) {
      console.error('Error checking Stripe availability:', error);
      setOrganizationHasStripe(false);
      setStripeAvailable(false);
      setPaymentMethod('manual');
    }
  };

  // Update stripe availability when selected family changes
  useEffect(() => {
    if (selectedFamily && organizationHasStripe) {
      const familySupportsStripe = selectedFamily.billing_preference === 'auto';
      setStripeAvailable(familySupportsStripe);
      
      // Auto-select payment method based on family preference
      if (familySupportsStripe) {
        setPaymentMethod('online');
      } else {
        setPaymentMethod('manual');
      }
    } else {
      setStripeAvailable(false);
      setPaymentMethod('manual');
    }
  }, [selectedFamily, organizationHasStripe]);

  const loadFamilies = async () => {
    if (!organizationId) return;

    setFamiliesLoading(true);
    try {
      const { data, error } = await supabase
        .from('families')
        .select(`
          id, 
          name,
          billing_preference,
          parents(email, is_primary)
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      
      // Transform the data to match our Family interface
      const familiesWithEmails = (data || []).map(family => {
        // Find the primary parent's email
        const primaryParent = family.parents?.find(parent => parent.is_primary);
        const email = primaryParent?.email || family.parents?.[0]?.email || '';
        
        return {
          id: family.id,
          name: family.name,
          primary_contact_email: email,
          billing_preference: family.billing_preference || 'manual'
        };
      });

      setFamilies(familiesWithEmails);
    } catch (error) {
      console.error('Error loading families:', error);
      toast.error('Failed to load families');
    } finally {
      setFamiliesLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFamily || !amount || !memo || !category || !organizationId) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Only check email for online payments
    if (paymentMethod === 'online' && !selectedFamily.primary_contact_email) {
      toast.error('Selected family has no email address. Please update their contact information first.');
      return;
    }

    setLoading(true);
    try {
      // Parse amount to cents
      const amountCents = parseCurrencyToCents(amount);
      
      if (amountCents <= 0) {
        toast.error('Please enter a valid amount greater than $0.00');
        setLoading(false);
        return;
      }

      if (paymentMethod === 'online') {
        // Online payment using Stripe
        console.log('Creating online payment with Stripe');

        const responseData = await callSupabaseFunction('create-one-time-payment', {
          amount_cents: amountCents,
          name: `${category} Charge`,
          description: `${category}: ${memo}`,
          customer_email: selectedFamily.primary_contact_email,
          success_url: `${window.location.origin}/payment-success`,
          cancel_url: `${window.location.origin}/admin`,
          organizationId: organizationId,
          metadata: {
            family_id: selectedFamily.id,
            family_name: selectedFamily.name,
            category: category,
            description: memo,
            created_via: 'quick_charge'
          }
        });

        const { url } = responseData;

        if (url) {
          // Copy payment link to clipboard
          await navigator.clipboard.writeText(url);
          toast.success(
            `Payment link sent to ${selectedFamily.name}! Link copied to clipboard.`,
            {
              duration: 5000,
            }
          );
        } else {
          throw new Error('No payment URL returned');
        }
      } else {
        // Manual charge using invoice service
        console.log('Creating manual charge invoice');

        // Create invoice for manual charge
        const invoiceData = {
          family_id: selectedFamily.id,
          organization_id: organizationId,
          total_cents: amountCents,
          due_date: new Date().toISOString().split('T')[0], // Today
          status: 'unpaid' as const,
        };

        console.log('Invoice data:', invoiceData);
        const invoice = await InvoiceService.createInvoice(invoiceData);

        if (!invoice) {
          throw new Error('Failed to create invoice - service returned null');
        }

        console.log('Invoice created successfully:', invoice);

        // Create comprehensive line item with all Quick Charge details
        const quickChargeId = `QC-${category.toUpperCase()}-${Date.now().toString().slice(-6)}`;
        const lineItemDescription = [
          `Quick Charge: ${quickChargeId}`,
          `Category: ${category}`,
          memo ? `Description: ${memo}` : null,
          `Payment Method: Manual`
        ].filter(Boolean).join(' | ');

        await InvoiceService.createInvoiceLineItem({
          invoice_id: invoice.id,
          description: lineItemDescription,
          amount_cents: amountCents,
        });

        toast.success(
          `Manual charge created for ${selectedFamily.name}! Check billing dashboard to track payment.`,
          {
            duration: 5000,
          }
        );
      }

      // Reset form and close modal
      setSelectedFamily(null);
      setAmount('');
      setMemo('');
      setCategory('');
      // Payment method will be reset automatically via useEffect when selectedFamily changes
      onOpenChange(false);

    } catch (error) {
      console.error('Error creating quick charge:', error);
      toast.error(`Failed to create quick charge: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Quick Charge
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Family Picker */}
          <div className="space-y-2">
            <Label htmlFor="family">Family *</Label>
            <Popover open={familyPickerOpen} onOpenChange={setFamilyPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={familyPickerOpen}
                  className="w-full justify-between"
                  disabled={familiesLoading}
                >
                  {selectedFamily ? selectedFamily.name : "Select family..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search families..." />
                  <CommandEmpty>
                    {familiesLoading ? "Loading families..." : "No families found."}
                  </CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-auto">
                    {families.map((family) => (
                      <CommandItem
                        key={family.id}
                        value={family.name}
                        onSelect={() => {
                          setSelectedFamily(family);
                          setFamilyPickerOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedFamily?.id === family.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span>{family.name}</span>
                            {family.billing_preference === 'auto' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Online</span>
                            )}
                          </div>
                          {!family.primary_contact_email && (
                            <span className="text-xs text-red-500">No email address</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Payment Method Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Payment Method *</Label>
            <RadioGroup 
              value={paymentMethod} 
              onValueChange={(value: 'online' | 'manual') => setPaymentMethod(value)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="online" id="online" disabled={!stripeAvailable} />
                <Label htmlFor="online" className="flex items-center gap-2 cursor-pointer">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                  <div className="flex flex-col">
                    <span className={stripeAvailable ? "text-gray-900" : "text-gray-400"}>
                      Online Payment (Stripe)
                    </span>
                    <span className="text-xs text-gray-500">
                      {stripeAvailable 
                        ? "Creates payment link and sends to family email" 
                        : !organizationHasStripe
                          ? "Requires Stripe Connect setup"
                          : selectedFamily?.billing_preference === 'manual'
                            ? "Family prefers manual payments"
                            : "Select a family to check availability"
                      }
                    </span>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="manual" id="manual" />
                <Label htmlFor="manual" className="flex items-center gap-2 cursor-pointer">
                  <FileText className="h-4 w-4 text-green-600" />
                  <div className="flex flex-col">
                    <span className="text-gray-900">Manual Charge</span>
                    <span className="text-xs text-gray-500">
                      Creates invoice for cash/check payment at front desk
                    </span>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
                $
              </div>
              <Input
                id="amount"
                type="text"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
                className="pl-8"
                required
                pattern="[0-9]+(\.[0-9]{1,2})?"
                title="Please enter a valid amount (e.g., 25.50)"
              />
            </div>
            {amount && parseFloat(amount) <= 0 && (
              <p className="text-sm text-red-600">Amount must be greater than $0.00</p>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Class">Class</SelectItem>
                <SelectItem value="Merchandise">Merchandise</SelectItem>
                <SelectItem value="Membership">Membership</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Memo */}
          <div className="space-y-2">
            <Label htmlFor="memo">Description *</Label>
            <Textarea
              id="memo"
              placeholder="Enter charge description..."
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={3}
              required
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedFamily || !amount || !memo || !category}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                paymentMethod === 'online' ? "Creating Payment Link..." : "Creating Invoice..."
              ) : (
                <>
                  {paymentMethod === 'online' ? (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Create Payment Link
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Create Manual Charge
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickChargeModal;